import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { RefreshCw, ShieldCheck, Eye, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useButtonAlertsPendingSync } from '../contexts/ButtonAlertsPendingSyncContext';
import ButtonAlertMiniMap, { type ButtonAlertMapMarker } from '../components/ButtonAlertMiniMap';
import AlertAudioPlayButton from '../components/AlertAudioPlayButton';
import {
  EU_ALARM_TYPE_LABELS,
  ESCUDO_URBANO_ALERT_TITLE,
  formatRelativeTime,
  resolveEuAlarmTypeLabel
} from '../utils/euEscudoUrbanoDisplay';
import { exportToPdf, exportToExcel } from '../utils/buttonAlertExport';
import '../components/Header.css';
import './ButtonAlertsDashboardPage.css';
import {
  BUTTON_ALERT_COMMENT_MAX_LEN,
  BUTTON_ALERT_COMMENT_MIN_LEN
} from '../constants/buttonAlertManagement';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';
const CAPTCHA_LEN = 5;
const CAPTCHA_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

function randomCaptchaCode(): string {
  let s = '';
  for (let i = 0; i < CAPTCHA_LEN; i++) {
    s += CAPTCHA_CHARS[Math.floor(Math.random() * CAPTCHA_CHARS.length)];
  }
  return s;
}

type AlertMetadata = {
  latitud?: number;
  longitud?: number;
  site_name?: string | null;
  site_address?: string | null;
  sound?: number | string | null;
  alarm_type_id?: number | string | null;
};

type HistoryRow = {
  id: number;
  deviceId?: number;
  siteId: number | null;
  alertType?: string;
  severity?: string;
  message: string;
  metadata?: AlertMetadata;
  /** Ruta del MP3 del perifoneo (alerta personalizada, sound 14). El backend la incluye en /history. */
  audioPath?: string | null;
  acknowledged?: boolean;
  acknowledgedAt?: string | null;
  acknowledgedBy?: number | null;
  acknowledgedByUserName?: string | null;
  comment?: string | null;
  createdAt?: string;
  siteName?: string | null;
  siteLatitude?: string | null;
  siteLongitude?: string | null;
  serviceName?: string | null;
};

type EuSiteStat = { siteId: number; siteName: string; count: number };
type AlarmTypeStat = { alarmTypeKey: number; count: number };

function euAlarmTypeLabel(key: number): string {
  return EU_ALARM_TYPE_LABELS[key] ?? 'Sin clasificar';
}

function getSiteDisplayName(row: HistoryRow): string {
  const n = row.siteName != null ? String(row.siteName).trim() : '';
  if (n) return n;
  // El JOIN con sites puede venir vacío (sitio sin nombre o inexistente); en ese caso
  // usamos el nombre del sitio capturado en el metadata de la alerta.
  const metaName = row.metadata?.site_name != null ? String(row.metadata.site_name).trim() : '';
  if (metaName) return metaName;
  if (row.siteId != null) return `Sitio #${row.siteId}`;
  return '—';
}

function getAlertCoords(row: HistoryRow): [number, number] | null {
  const m = row.metadata;
  if (m && typeof m.latitud === 'number' && typeof m.longitud === 'number') {
    return [m.latitud, m.longitud];
  }
  const lat = row.siteLatitude != null ? parseFloat(row.siteLatitude) : NaN;
  const lng = row.siteLongitude != null ? parseFloat(row.siteLongitude) : NaN;
  if (Number.isFinite(lat) && Number.isFinite(lng)) return [lat, lng];
  return null;
}

function rowTypeLabel(row: HistoryRow): string {
  const m = row.metadata;
  return resolveEuAlarmTypeLabel({
    alarmTypeId: m?.alarm_type_id != null && m.alarm_type_id !== '' ? Number(m.alarm_type_id) : undefined,
    sound: m?.sound != null && m.sound !== '' ? Number(m.sound) : undefined,
    message: row.message,
    alertType: row.alertType
  });
}

function getRowSound(row: HistoryRow): number | undefined {
  const s = row.metadata?.sound;
  if (s == null || s === '') return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

/** Alerta personalizada (perifoneo, sound 14) con MP3 disponible → se puede reproducir cuando se desee. */
function isReplayablePersonalizedRow(row: HistoryRow): boolean {
  return getRowSound(row) === 14 && !!(row.audioPath && String(row.audioPath).trim());
}

function getSiteAddressLine(row: HistoryRow): string {
  const m = row.metadata;
  const addr = m?.site_address != null ? String(m.site_address).trim() : '';
  return addr || '—';
}

function formatHistoryDate(createdAt?: string): string {
  if (!createdAt) return '—';
  const dt = new Date(createdAt);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
}

/**
 * Clave de tipo de alarma para una fila, replicando la lógica del backend
 * (metadata.alarm_type_id → sound → 0). Se usa para derivar el resumen por
 * tipo a partir de las filas exportadas.
 */
function deriveAlarmTypeKey(row: HistoryRow): number {
  const m = row.metadata;
  const atid = m?.alarm_type_id;
  if (atid != null && atid !== '') {
    const n = Number(atid);
    if (Number.isFinite(n)) return n;
  }
  const sound = m?.sound;
  if (sound != null && sound !== '') {
    const n = Number(sound);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** Resumen de alertas por sitio derivado de las filas exportadas. */
function deriveByEuSite(rows: HistoryRow[]): EuSiteStat[] {
  const map = new Map<number, EuSiteStat>();
  for (const r of rows) {
    if (r.siteId == null) continue;
    const existing = map.get(r.siteId);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(r.siteId, { siteId: r.siteId, siteName: getSiteDisplayName(r), count: 1 });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

/** Resumen de alertas por tipo derivado de las filas exportadas. */
function deriveByAlarmType(rows: HistoryRow[]): AlarmTypeStat[] {
  const map = new Map<number, number>();
  for (const r of rows) {
    const key = deriveAlarmTypeKey(r);
    map.set(key, (map.get(key) ?? 0) + 1);
  }
  return Array.from(map.entries())
    .map(([alarmTypeKey, count]) => ({ alarmTypeKey, count }))
    .sort((a, b) => b.count - a.count);
}

const ButtonAlertsDashboardPage: React.FC = () => {
  const { token } = useAuth();
  const { notifyPendingButtonAlertsChanged } = useButtonAlertsPendingSync();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [byEuSite, setByEuSite] = useState<EuSiteStat[]>([]);
  const [byAlarmType, setByAlarmType] = useState<AlarmTypeStat[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const [manageTarget, setManageTarget] = useState<HistoryRow | null>(null);
  const [detailTarget, setDetailTarget] = useState<HistoryRow | null>(null);
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [manageComment, setManageComment] = useState('');
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportModalRef = useRef<HTMLDivElement>(null);
  const manageModalRef = useRef<HTMLDivElement>(null);
  const detailModalRef = useRef<HTMLDivElement>(null);

  const loadAll = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { page, pageSize };
      if (from.trim()) params.from = from.trim();
      if (to.trim()) params.to = to.trim();

      const statsParams: Record<string, string> = {};
      if (from.trim()) statsParams.from = from.trim();
      if (to.trim()) statsParams.to = to.trim();

      const [histRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/button-alerts/history`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        }),
        axios.get(`${API_URL}/button-alerts/stats/eu`, {
          headers: { Authorization: `Bearer ${token}` },
          params: statsParams
        })
      ]);

      const h = histRes.data?.data as { rows?: HistoryRow[]; total?: number } | undefined;
      setRows(h && Array.isArray(h.rows) ? h.rows : []);
      setTotal(h && typeof h.total === 'number' ? h.total : 0);

      const s = statsRes.data?.data as { byEuSite?: EuSiteStat[]; byAlarmType?: AlarmTypeStat[] } | undefined;
      setByEuSite(s && Array.isArray(s.byEuSite) ? s.byEuSite : []);
      setByAlarmType(s && Array.isArray(s.byAlarmType) ? s.byAlarmType : []);
    } catch {
      setError('No se pudieron cargar los datos. Compruebe la sesión y la API.');
      setRows([]);
      setTotal(0);
      setByEuSite([]);
      setByAlarmType([]);
    } finally {
      setLoading(false);
    }
  }, [token, page, from, to]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const openManageModal = useCallback((row: HistoryRow) => {
    setCaptchaCode(randomCaptchaCode());
    setCaptchaInput('');
    setManageComment('');
    setManageTarget(row);
  }, []);

  const closeManageModal = useCallback(() => {
    setManageTarget(null);
    setCaptchaInput('');
    setManageComment('');
    setCaptchaCode('');
  }, []);

  const openDetailModal = useCallback((row: HistoryRow) => {
    setDetailTarget(row);
  }, []);

  const closeDetailModal = useCallback(() => {
    setDetailTarget(null);
  }, []);

  useEffect(() => {
    if (!manageTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeManageModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [manageTarget, closeManageModal]);

  useEffect(() => {
    if (!manageTarget) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (manageModalRef.current?.contains(t)) return;
      closeManageModal();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [manageTarget, closeManageModal]);

  useEffect(() => {
    if (!detailTarget) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDetailModal();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detailTarget, closeDetailModal]);

  useEffect(() => {
    if (!detailTarget) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (detailModalRef.current?.contains(t)) return;
      closeDetailModal();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [detailTarget, closeDetailModal]);

  useEffect(() => {
    if (!showExportModal) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowExportModal(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [showExportModal]);

  useEffect(() => {
    if (!showExportModal) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (exportModalRef.current?.contains(t)) return;
      setShowExportModal(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showExportModal]);

  const handleExport = useCallback(async (format: 'pdf' | 'excel') => {
    if (!token) return;
    setExporting(true);
    try {
      // El backend limita el pageSize del historial a 100 registros por página.
      // Para exportar todo el rango solicitado paginamos y acumulamos todas las páginas,
      // en lugar de pedir un pageSize gigante (que se recorta a 100 y solo devolvía las
      // alertas más recientes, dejando el reporte incompleto).
      const EXPORT_PAGE_SIZE = 100;
      const dateParams: Record<string, string> = {};
      if (from.trim()) dateParams.from = from.trim();
      if (to.trim()) dateParams.to = to.trim();

      const fetchHistoryPage = async (pageNum: number) =>
        axios.get(`${API_URL}/button-alerts/history`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { ...dateParams, page: pageNum, pageSize: EXPORT_PAGE_SIZE }
        });

      const firstHistRes = await fetchHistoryPage(1);

      const firstH = firstHistRes.data?.data as
        | { rows?: HistoryRow[]; total?: number; pageSize?: number }
        | undefined;
      const totalCount = firstH && typeof firstH.total === 'number' ? firstH.total : 0;
      const effectivePageSize =
        firstH && typeof firstH.pageSize === 'number' && firstH.pageSize > 0
          ? firstH.pageSize
          : EXPORT_PAGE_SIZE;

      const allRows: HistoryRow[] = firstH && Array.isArray(firstH.rows) ? [...firstH.rows] : [];

      const totalPagesToFetch = Math.max(1, Math.ceil(totalCount / effectivePageSize));
      if (totalPagesToFetch > 1) {
        const remaining = await Promise.all(
          Array.from({ length: totalPagesToFetch - 1 }, (_, i) => fetchHistoryPage(i + 2))
        );
        for (const res of remaining) {
          const hp = res.data?.data as { rows?: HistoryRow[] } | undefined;
          if (hp && Array.isArray(hp.rows)) allRows.push(...hp.rows);
        }
      }

      // El resumen (por sitio y por tipo) se calcula a partir de las filas realmente
      // exportadas, no del endpoint /stats/eu. Ese endpoint solo cuenta sitios clasificados
      // como Escudo Urbano (INNER JOIN por nombre de servicio) y podía venir vacío, dejando
      // el PDF/Excel sin resumen. Derivándolo de las filas garantizamos que el resumen
      // siempre esté presente, coincida con el detalle exportado y muestre el nombre del sitio.
      const siteStats = deriveByEuSite(allRows);
      const typeStats = deriveByAlarmType(allRows);

      const payload = {
        rows: allRows,
        byEuSite: siteStats,
        byAlarmType: typeStats,
        total: totalCount,
        from,
        to,
        alarmTypeLabel: euAlarmTypeLabel,
        siteDisplayName: getSiteDisplayName,
        rowTypeLabel,
      };

      if (format === 'pdf') {
        exportToPdf(payload);
      } else {
        exportToExcel(payload);
      }

      toast.success(format === 'pdf' ? 'PDF generado correctamente' : 'Excel generado correctamente');
      setShowExportModal(false);
    } catch {
      toast.error('Error al generar la exportación');
    } finally {
      setExporting(false);
    }
  }, [token, from, to]);

  const trimmedManageComment = manageComment.trim();
  const captchaOk = captchaInput.length === CAPTCHA_LEN && captchaInput === captchaCode;
  const commentOk =
    trimmedManageComment.length >= BUTTON_ALERT_COMMENT_MIN_LEN &&
    trimmedManageComment.length <= BUTTON_ALERT_COMMENT_MAX_LEN;
  const canConfirmManage = captchaOk && commentOk && !manageSubmitting;

  const confirmManage = useCallback(async () => {
    if (!manageTarget || !token) return;
    const commentText = manageComment.trim();
    if (
      captchaInput !== captchaCode ||
      commentText.length < BUTTON_ALERT_COMMENT_MIN_LEN ||
      commentText.length > BUTTON_ALERT_COMMENT_MAX_LEN
    ) {
      return;
    }
    setManageSubmitting(true);
    const id = manageTarget.id;
    try {
      await axios.patch(
        `${API_URL}/button-alerts/${id}/acknowledge`,
        { comment: commentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Alarma gestionada');
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, acknowledged: true, comment: commentText } : r))
      );
      notifyPendingButtonAlertsChanged();
      closeManageModal();
      void loadAll();
    } catch {
      toast.error('No se pudo registrar la gestión');
    } finally {
      setManageSubmitting(false);
    }
  }, [
    manageTarget,
    token,
    captchaInput,
    captchaCode,
    manageComment,
    closeManageModal,
    loadAll,
    notifyPendingButtonAlertsChanged,
  ]);

  const manageCard =
    manageTarget != null
      ? {
          siteName: getSiteDisplayName(manageTarget),
          address: getSiteAddressLine(manageTarget),
          alarmLabel: rowTypeLabel(manageTarget),
          when: formatRelativeTime(manageTarget.createdAt)
        }
      : null;

  const mapMarkers: ButtonAlertMapMarker[] = useMemo(() => {
    const out: ButtonAlertMapMarker[] = [];
    for (const r of rows) {
      const c = getAlertCoords(r);
      if (!c) continue;
      out.push({
        id: r.id,
        lat: c[0],
        lng: c[1],
        label: `${getSiteDisplayName(r)} · ${rowTypeLabel(r)}`,
        selected: selectedId === r.id
      });
    }
    return out;
  }, [rows, selectedId]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="button-alerts-dashboard">
      <header className="bad-header">
        <div className="bad-header-text">
          <h1>Gestión de alertas de botón</h1>
          <p className="bad-sub">
            Consulta el historial de alertas de botón, revisa totales por punto Escudo Urbano y por tipo de alarma, y
            filtra por fechas.
          </p>
        </div>
        <div className="bad-filters">
          <label className="bad-filter">
            <span>Desde</span>
            <input
              type="date"
              className="bad-date-input"
              value={from}
              onChange={(e) => { setPage(1); setFrom(e.target.value); }}
            />
          </label>
          <label className="bad-filter">
            <span>Hasta</span>
            <input
              type="date"
              className="bad-date-input"
              value={to}
              onChange={(e) => { setPage(1); setTo(e.target.value); }}
            />
          </label>
          <button type="button" className="bad-btn bad-btn-primary" onClick={() => void loadAll()} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'bad-spin' : ''} />
            Actualizar
          </button>
          <button type="button" className="bad-btn bad-btn-ghost" onClick={() => setShowExportModal(true)} disabled={loading || rows.length === 0}>
            <Download size={16} />
            Exportar
          </button>
        </div>
      </header>

      {error && <div className="bad-banner bad-banner-error">{error}</div>}

      <div className="bad-layout">
        <aside className="bad-aside">
          <section className="bad-card">
            <h2>Ubicación</h2>
            <p className="bad-hint">Seleccione una fila para centrar el mapa en esa alerta.</p>
            <ButtonAlertMiniMap markers={mapMarkers} focusId={selectedId} />
          </section>

          <section className="bad-card">
            <h2>Actuaciones por EU</h2>
            <div className="bad-table-wrap">
              <table className="bad-table">
                <thead>
                  <tr>
                    <th>Punto / sitio</th>
                    <th className="bad-num">Alertas</th>
                  </tr>
                </thead>
                <tbody>
                  {byEuSite.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="bad-empty">
                        Sin datos en el rango
                      </td>
                    </tr>
                  ) : (
                    byEuSite.map((s) => (
                      <tr key={s.siteId}>
                        <td>{s.siteName || `Sitio #${s.siteId}`}</td>
                        <td className="bad-num">{s.count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="bad-card">
            <h2>Top por tipo de alarma</h2>
            <p className="bad-hint">Tipos de alarma Escudo Urbano (por ejemplo robo, drogas, violencia intrafamiliar).</p>
            <div className="bad-table-wrap">
              <table className="bad-table">
                <thead>
                  <tr>
                    <th>Tipo</th>
                    <th className="bad-num">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {byAlarmType.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="bad-empty">
                        Sin datos en el rango
                      </td>
                    </tr>
                  ) : (
                    byAlarmType.map((t) => (
                      <tr key={t.alarmTypeKey}>
                        <td>{euAlarmTypeLabel(t.alarmTypeKey)}</td>
                        <td className="bad-num">{t.count}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </aside>

        <main className="bad-main">
          <section className="bad-card bad-card-fill">
            <div className="bad-card-head">
              <h2>Historial de alertas</h2>
              <span className="bad-meta">
                {total} registro{total !== 1 ? 's' : ''} · Página {page} de {totalPages}
              </span>
            </div>
            <div className="bad-table-wrap bad-table-scroll">
              <table className="bad-table bad-table-dense">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Sitio</th>
                    <th>Tipo EU</th>
                    <th className="bad-status-th">Estado</th>
                    <th>Gestionó</th>
                    <th>Comentario</th>
                    <th className="bad-actions-col bad-actions-th">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="bad-empty">
                        {loading ? 'Cargando…' : 'No hay alertas en esta página.'}
                      </td>
                    </tr>
                  ) : (
                    rows.map((r) => {
                      const dt = r.createdAt ? new Date(r.createdAt) : null;
                      const dateStr =
                        dt && !Number.isNaN(dt.getTime())
                          ? dt.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })
                          : '—';
                      return (
                        <tr
                          key={r.id}
                          className={selectedId === r.id ? 'bad-row-selected' : undefined}
                          onClick={() => setSelectedId(r.id)}
                        >
                          <td>{dateStr}</td>
                          <td>{getSiteDisplayName(r)}</td>
                          <td>{rowTypeLabel(r)}</td>
                          <td className="bad-status-cell">
                            {r.acknowledged ? (
                              <span className="bad-status-chip bad-status-chip--done">Gestionada</span>
                            ) : (
                              <span className="bad-status-chip bad-status-chip--pending">Pendiente</span>
                            )}
                          </td>
                          <td>
                            {r.acknowledged
                              ? r.acknowledgedByUserName && String(r.acknowledgedByUserName).trim()
                                ? String(r.acknowledgedByUserName).trim()
                                : '—'
                              : '—'}
                          </td>
                          <td
                            className="bad-comment-cell bad-comment-cell--interactive"
                            title="Clic para ver el texto completo en una ventana"
                            onClick={(e) => {
                              e.stopPropagation();
                              openDetailModal(r);
                            }}
                          >
                            {r.comment && String(r.comment).trim() ? (
                              <span className="bad-comment-preview">{String(r.comment).trim()}</span>
                            ) : (
                              <span className="bad-comment-empty">—</span>
                            )}
                          </td>
                          <td className="bad-actions-col" onClick={(e) => e.stopPropagation()}>
                            <div className="bad-action-btns">
                              {isReplayablePersonalizedRow(r) && (
                                <AlertAudioPlayButton
                                  buttonAlertId={r.id}
                                  token={token}
                                  sound={getRowSound(r)}
                                  size={14}
                                  className="bad-btn bad-btn-ghost bad-btn-sm bad-action-btn"
                                  iconClassName=""
                                />
                              )}
                              {!r.acknowledged && (
                                <button
                                  type="button"
                                  className="bad-btn bad-btn-primary bad-btn-sm bad-action-btn"
                                  disabled={manageSubmitting && manageTarget?.id === r.id}
                                  onClick={() => openManageModal(r)}
                                  title="Registrar gestión de la alerta"
                                >
                                  Gestionar
                                </button>
                              )}
                              <button
                                type="button"
                                className="bad-btn bad-btn-ghost bad-btn-sm bad-action-btn"
                                onClick={() => openDetailModal(r)}
                                title="Ver mensaje de la alerta y comentario de gestión completos"
                              >
                                <Eye size={14} aria-hidden />
                                Ver detalle
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            <div className="bad-pagination">
              <button
                type="button"
                className="bad-btn bad-btn-ghost"
                disabled={page <= 1 || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </button>
              <button
                type="button"
                className="bad-btn bad-btn-ghost"
                disabled={page >= totalPages || loading}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </section>
        </main>
      </div>

      {manageTarget &&
        createPortal(
          <div
            ref={manageModalRef}
            className="header-alerts-manage-modal__backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeManageModal();
            }}
          >
            <div
              className="header-alerts-manage-modal"
              role="dialog"
              aria-labelledby="bad-alerts-manage-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="header-alerts-manage-modal__header">
                <ShieldCheck size={22} className="header-alerts-manage-modal__header-icon" aria-hidden />
                <h2 id="bad-alerts-manage-title" className="header-alerts-manage-modal__title">
                  Confirmar gestión
                </h2>
                <p className="header-alerts-manage-modal__subtitle">
                  Completa la verificación y un comentario para registrar que atendiste esta alerta.
                </p>
              </div>
              <div className="header-alerts-manage-modal__context">
                {manageCard && manageTarget && (
                  <>
                    <div className="header-alerts-manage-modal__context-headline">
                      <span className="header-alerts-manage-modal__context-kind">{ESCUDO_URBANO_ALERT_TITLE}</span>
                      {manageCard.when && (
                        <span className="header-alerts-manage-modal__context-when">{manageCard.when}</span>
                      )}
                    </div>
                    <span className="header-alerts-manage-modal__context-sublabel">Ubicación</span>
                    <span className="header-alerts-manage-modal__context-value">{manageCard.siteName}</span>
                    <span className="header-alerts-manage-modal__context-sublabel">Dirección</span>
                    <span className="header-alerts-manage-modal__context-value">{manageCard.address}</span>
                    <span className="header-alerts-manage-modal__context-sublabel">Tipo de alarma</span>
                    <span className="header-alerts-manage-modal__context-alarm">{manageCard.alarmLabel}</span>
                    <span className="header-alerts-manage-modal__context-sublabel">Mensaje</span>
                    <span className="header-alerts-manage-modal__context-msg">{manageTarget.message}</span>
                  </>
                )}
              </div>
              <div className="header-alerts-manage-modal__field">
                <label className="header-alerts-manage-modal__label" htmlFor="bad-alerts-dash-captcha">
                  Código de verificación{' '}
                  <span className="header-alerts-manage-modal__hint">(copia el código)</span>
                </label>
                <div className="header-alerts-manage-modal__captcha-row" aria-hidden>
                  {captchaCode.split('').map((ch, i) => (
                    <span key={`${captchaCode}-${ch}-${i}`} className="header-alerts-manage-modal__captcha-char">
                      {ch}
                    </span>
                  ))}
                </div>
                <input
                  id="bad-alerts-dash-captcha"
                  className="header-alerts-manage-modal__input"
                  type="text"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck={false}
                  maxLength={CAPTCHA_LEN}
                  placeholder={`Escribe los ${CAPTCHA_LEN} caracteres`}
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                />
                {captchaInput.length > 0 && !captchaOk && (
                  <span className="header-alerts-manage-modal__error">El código no coincide.</span>
                )}
              </div>
              <div className="header-alerts-manage-modal__field">
                <label className="header-alerts-manage-modal__label" htmlFor="bad-alerts-dash-comment">
                  Comentario de gestión{' '}
                  <span className="header-alerts-manage-modal__hint">
                    (mín. {BUTTON_ALERT_COMMENT_MIN_LEN}, máx. {BUTTON_ALERT_COMMENT_MAX_LEN} caracteres)
                  </span>
                </label>
                <textarea
                  id="bad-alerts-dash-comment"
                  className="header-alerts-manage-modal__textarea"
                  rows={4}
                  maxLength={BUTTON_ALERT_COMMENT_MAX_LEN}
                  placeholder="Describe brevemente la acción tomada…"
                  value={manageComment}
                  onChange={(e) => setManageComment(e.target.value)}
                />
                <span className="header-alerts-manage-modal__counter">
                  {trimmedManageComment.length} / {BUTTON_ALERT_COMMENT_MAX_LEN} · mín.{' '}
                  {BUTTON_ALERT_COMMENT_MIN_LEN}
                </span>
              </div>
              <div className="header-alerts-manage-modal__actions">
                <button
                  type="button"
                  className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--ghost"
                  onClick={closeManageModal}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="header-alerts-manage-modal__btn header-alerts-manage-modal__btn--primary"
                  disabled={!canConfirmManage}
                  onClick={() => void confirmManage()}
                >
                  {manageSubmitting ? 'Guardando…' : 'Confirmar gestión'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {detailTarget &&
        createPortal(
          <div
            ref={detailModalRef}
            className="bad-detail-backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) closeDetailModal();
            }}
          >
            <div
              className="bad-detail-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="bad-detail-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="bad-detail-header">
                <h2 id="bad-detail-title" className="bad-detail-title">
                  {detailTarget.acknowledged ? 'Gestión registrada' : 'Detalle de alerta'}
                </h2>
                <p className="bad-detail-sub">
                  {detailTarget.acknowledged
                    ? 'Revise el comentario completo y el contexto de la alarma. Si el texto es largo, desplace este panel hacia abajo para verlo íntegro.'
                    : 'Vista previa del contenido de la alerta. Use «Gestionar» para registrar la atención.'}
                </p>
              </div>
              <div className="bad-detail-body">
                <div className="bad-detail-grid">
                  <span className="bad-detail-label">Fecha</span>
                  <span className="bad-detail-value">{formatHistoryDate(detailTarget.createdAt)}</span>

                  <span className="bad-detail-label">Sitio</span>
                  <span className="bad-detail-value">{getSiteDisplayName(detailTarget)}</span>

                  <span className="bad-detail-label">Dirección</span>
                  <span className="bad-detail-value">{getSiteAddressLine(detailTarget)}</span>

                  <span className="bad-detail-label">Tipo EU</span>
                  <span className="bad-detail-value">{rowTypeLabel(detailTarget)}</span>

                  <span className="bad-detail-label">Severidad</span>
                  <span className="bad-detail-value">{detailTarget.severity ?? '—'}</span>

                  <span className="bad-detail-label">Estado</span>
                  <span className="bad-detail-value">
                    {detailTarget.acknowledged ? (
                      <span className="bad-status-chip bad-status-chip--done">Gestionada</span>
                    ) : (
                      <span className="bad-status-chip bad-status-chip--pending">Pendiente</span>
                    )}
                  </span>

                  {detailTarget.acknowledged ? (
                    <>
                      <span className="bad-detail-label">Gestionó</span>
                      <span className="bad-detail-value">
                        {detailTarget.acknowledgedByUserName &&
                        String(detailTarget.acknowledgedByUserName).trim()
                          ? String(detailTarget.acknowledgedByUserName).trim()
                          : '—'}
                      </span>

                      <span className="bad-detail-label">Fecha de gestión</span>
                      <span className="bad-detail-value">
                        {formatHistoryDate(detailTarget.acknowledgedAt ?? undefined)}
                      </span>
                    </>
                  ) : null}
                </div>

                {isReplayablePersonalizedRow(detailTarget) && (
                  <div className="bad-detail-block">
                    <span className="bad-detail-label bad-detail-label--block">Audio de perifoneo</span>
                    <div className="bad-detail-audio-row">
                      <AlertAudioPlayButton
                        buttonAlertId={detailTarget.id}
                        token={token}
                        sound={getRowSound(detailTarget)}
                        size={18}
                        className="bad-btn bad-btn-ghost bad-btn-sm bad-action-btn"
                        iconClassName=""
                      />
                      <span className="bad-detail-audio-hint">
                        Reproducir el audio personalizado de esta alerta.
                      </span>
                    </div>
                  </div>
                )}

                {detailTarget.acknowledged ? (
                  <>
                    <div className="bad-detail-block bad-detail-block--comment-first">
                      <span className="bad-detail-label bad-detail-label--block">Comentario de gestión</span>
                      {detailTarget.comment && String(detailTarget.comment).trim() ? (
                        <>
                          <span className="bad-detail-char-count" aria-live="polite">
                            {String(detailTarget.comment).trim().length} caracteres
                          </span>
                          <pre className="bad-detail-pre bad-detail-pre--comment">
                            {String(detailTarget.comment).trim()}
                          </pre>
                        </>
                      ) : (
                        <pre className="bad-detail-pre bad-detail-pre--comment">
                          Sin comentario registrado en el sistema.
                        </pre>
                      )}
                    </div>
                    <div className="bad-detail-block">
                      <span className="bad-detail-label bad-detail-label--block">Mensaje de la alerta</span>
                      <pre className="bad-detail-pre">{detailTarget.message || '—'}</pre>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bad-detail-block">
                      <span className="bad-detail-label bad-detail-label--block">Mensaje de la alerta</span>
                      <pre className="bad-detail-pre">{detailTarget.message || '—'}</pre>
                    </div>
                    <div className="bad-detail-block">
                      <span className="bad-detail-label bad-detail-label--block">Comentario de gestión</span>
                      <p className="bad-detail-pending-note">
                        Aún no hay comentario de gestión. Al confirmar la gestión, el texto completo quedará
                        disponible aquí y en el historial.
                      </p>
                    </div>
                  </>
                )}
              </div>
              <div className="bad-detail-footer">
                <button type="button" className="bad-btn bad-btn-primary" onClick={closeDetailModal}>
                  Cerrar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      {showExportModal &&
        createPortal(
          <div
            className="bad-export-backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setShowExportModal(false);
            }}
          >
            <div
              ref={exportModalRef}
              className="bad-export-dialog"
              role="dialog"
              aria-labelledby="bad-export-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="bad-export-header">
                <Download size={22} className="bad-export-header-icon" aria-hidden />
                <h2 id="bad-export-title" className="bad-export-title">Exportar alertas</h2>
                <p className="bad-export-sub">
                  Selecciona el formato de exportación. Se incluirán todas las alertas
                  {from || to ? ' del rango de fechas seleccionado' : ''} y las estadísticas.
                </p>
              </div>
              <div className="bad-export-options">
                <button
                  type="button"
                  className="bad-export-option"
                  disabled={exporting}
                  onClick={() => void handleExport('pdf')}
                >
                  <FileText size={32} className="bad-export-option-icon bad-export-option-icon--pdf" />
                  <span className="bad-export-option-label">PDF</span>
                  <span className="bad-export-option-desc">Tabla legible con estadísticas</span>
                </button>
                <button
                  type="button"
                  className="bad-export-option"
                  disabled={exporting}
                  onClick={() => void handleExport('excel')}
                >
                  <FileSpreadsheet size={32} className="bad-export-option-icon bad-export-option-icon--excel" />
                  <span className="bad-export-option-label">Excel</span>
                  <span className="bad-export-option-desc">Hojas con alertas y estadísticas</span>
                </button>
              </div>
              {exporting && <div className="bad-export-loading">Generando archivo…</div>}
              <div className="bad-export-footer">
                <button
                  type="button"
                  className="bad-btn bad-btn-ghost"
                  onClick={() => setShowExportModal(false)}
                  disabled={exporting}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ButtonAlertsDashboardPage;
